import * as React from 'react'
import { isEmpty } from 'ramda'

import { useBuffer } from './Buffer'

/* TYPES */

type ChangeEvent = React.ChangeEvent<HTMLInputElement>

export interface TextEntryConfig {
  initialValue?: string
  onChange: (event: ChangeEvent) => void
}

export interface TextEntryApi {
  clear: () => void
  empty: boolean
  onChange: (event: ChangeEvent) => void
  reset: () => void
  value: string
}

interface Props extends TextEntryConfig {
  children?: React.ReactNode
}

interface RenderProps extends TextEntryConfig {
  children: (props: TextEntryApi) => {}
}

/* HOOK */

export const useTextEntry = ({
  initialValue = '',
  onChange = () => {},
}: TextEntryConfig) => {
  const { value, update, clear, reset } = useBuffer({ initialValue })

  return {
    clear: () => {
      onChange({ target: { value: '' } } as ChangeEvent)
      clear()
    },
    empty: isEmpty(value),
    onChange: (event: ChangeEvent) => {
      onChange(event)
      update(event.target.value)
    },
    reset: () => {
      onChange({ target: { value } } as ChangeEvent)
      reset()
    },
    value,
  }
}

/* DECORATOR */

export const withTextEntry = <T extends {}>(config: TextEntryConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { textEntry: TextEntryConfig }) => (
  <Component
    {...props}
    textEntry={useTextEntry({ ...config, ...props.textEntry })}
  />
)

/* RENDER PROP */

export const TextEntry = ({ children, ...props }: RenderProps) => {
  const textEntry = useTextEntry(props)

  return typeof children === 'function' ? children(textEntry) : children
}

/* CONTEXT */

const noop = (method: string) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<TextEntryProvider>\``
  )

export const TextEntryContext = React.createContext({
  value: '',
  empty: true,
  clear: noop('clear'),
  reset: noop('reset'),
})

export const TextEntryProvider = ({ children, ...props }: Props) => (
  <TextEntryContext.Provider value={useTextEntry(props)}>
    {children}
  </TextEntryContext.Provider>
)

export const consumeTextEntry = () => React.useContext(TextEntryContext)
